const BASE_URL = import.meta.env.VITE_API_URL ?? "/api";

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

function getToken(): string | null {
  return localStorage.getItem("accessToken");
}

// Access tokens last 15 minutes. Rather than letting a page silently fail
// once one expires, every authenticated call goes through authFetch, which on
// a 401 exchanges the stored refresh token for a new pair and retries once.
// Concurrent 401s share one in-flight refresh so a page firing several
// queries at once doesn't race itself into a logout.
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return false;
    const body = await res.json() as { accessToken: string; refreshToken?: string };
    localStorage.setItem("accessToken", body.accessToken);
    if (body.refreshToken) localStorage.setItem("refreshToken", body.refreshToken);
    return true;
  } catch {
    return false;
  }
}

export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= refreshTokens().finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

function sessionExpired() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  if (!window.location.pathname.startsWith("/login") && !window.location.pathname.startsWith("/public")) {
    window.location.assign("/login");
  }
}

/**
 * fetch() with the bearer token attached and transparent refresh on 401.
 * Use this instead of a bare fetch for any authenticated call that can't go
 * through `api` (file uploads, PDF streams).
 */
export async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const withToken = (): RequestInit => {
    const token = getToken();
    const headers = new Headers(init.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return { ...init, headers };
  };

  let response = await fetch(url, withToken());
  // Only login/refresh themselves are exempt. /auth/me must be retried: it is
  // what App calls on every page load, so skipping it sent anyone who
  // reloaded after 15 minutes straight back to the login screen.
  const exempt = /\/auth\/(login|refresh)$/.test(url);
  if (response.status === 401 && localStorage.getItem("refreshToken") && !exempt) {
    if (await refreshSession()) {
      response = await fetch(url, withToken());
    } else {
      sessionExpired();
    }
  }
  return response;
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  const response = await authFetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: "Unknown error" })) as { error?: string };
    throw new ApiError(response.status, body.error ?? response.statusText);
  }

  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

export { ApiError };
